import { EntityCostReport } from '../model/skucost/entity-cost-report';
import { EntityLine } from '../model/skucost/entity-line';
import { EntityLinePart } from '../model/skucost/entity-line-part';
import { EntityLineSku } from '../model/skucost/entity-line-sku';

export type CostMappingStatus = 'paperPilot' | 'warning' | 'ok';

export interface CostMappingIssues {
  paperPilot: string[];
  warnings: string[];
}

export class CostMappingUtils {

  static readonly UNMAPPED_KEY_MESSAGE = 'has not been mapped to a PRMS key';
  static readonly NO_PRODUCTS_MESSAGE = 'does not have product mappings';

  static needsPaperPilot(message: string | undefined | null): boolean {
    return !!message && (message.includes(CostMappingUtils.UNMAPPED_KEY_MESSAGE) || message.includes(CostMappingUtils.NO_PRODUCTS_MESSAGE));
  }

  static isNegativeCost(sku: EntityLineSku): boolean {
    return (sku.cost ?? 0) < 0;
  }

  static skuStatus(sku: EntityLineSku): CostMappingStatus {
    if (sku.costMappingMissing && !!sku.prmsLookupKey && CostMappingUtils.needsPaperPilot(sku.statusMessage)) {
      return 'paperPilot';
    }
    if (sku.costMappingMissing || CostMappingUtils.isNegativeCost(sku)) {
      return 'warning';
    }
    return 'ok';
  }

  static partStatus(part: EntityLinePart): CostMappingStatus {
    return part.costMappingMissing ? 'warning' : 'ok';
  }

  static lineStatus(line: EntityLine): CostMappingStatus {
    const statuses: CostMappingStatus[] = [
      ...(line.skus || []).map((sku) => CostMappingUtils.skuStatus(sku)),
      ...(line.parts || []).map((part) => CostMappingUtils.partStatus(part)),
    ];
    if (statuses.includes('paperPilot')) {
      return 'paperPilot';
    }
    return statuses.includes('warning') ? 'warning' : 'ok';
  }

  static rowClass(status: CostMappingStatus): string {
    switch (status) {
      case 'paperPilot':
        return 'costMappingPaperPilotRow';
      case 'warning':
        return 'costMappingWarningRow';
      default:
        return 'costMappingValidRow';
    }
  }

  static iconClass(status: CostMappingStatus): string {
    return status === 'paperPilot' ? 'size-16 costMappingPaperPilotIcon' : 'size-16 costMappingWarningIcon';
  }

  static skuTooltip(sku: EntityLineSku): string {
    if (CostMappingUtils.skuStatus(sku) === 'paperPilot') {
      return 'Needs a PRMS key mapping - copy this line to a paper pilot';
    }
    if (CostMappingUtils.isNegativeCost(sku) && !sku.costMappingMissing) {
      return 'Negative cost - verify this deduct with applications';
    }
    return 'Cost warning - a cost of $0 is being used, verify before approving';
  }

  static partTooltip(): string {
    return 'Cost warning - a cost of $0 is being used, verify before approving';
  }

  static lineTooltip(status: CostMappingStatus): string {
    return status === 'paperPilot' ? 'This line has SKUs that need a PRMS key mapping' : 'This line has cost warnings to verify';
  }

  static lineIssues(report: EntityCostReport | undefined): CostMappingIssues {
    const issues: CostMappingIssues = { paperPilot: [], warnings: [] };
    for (const line of report?.configuredEntityLines || []) {
      for (const sku of line.skus || []) {
        const status = CostMappingUtils.skuStatus(sku);
        if (status === 'ok') {
          continue;
        }
        const detail = sku.statusMessage || (CostMappingUtils.isNegativeCost(sku) ? 'has a negative cost' : 'cost could not be determined');
        const text = 'Line ' + line.lineId + ' - ' + sku.skuSlot + ' ' + sku.skuValue + ': ' + detail;
        (status === 'paperPilot' ? issues.paperPilot : issues.warnings).push(text);
      }
    }
    for (const line of report?.partAndRiserLines || []) {
      for (const part of line.parts || []) {
        if (part.costMappingMissing) {
          issues.warnings.push('Line ' + line.lineId + ' - ' + part.productId + ': ' + (part.statusMessage || 'cost could not be determined'));
        }
      }
    }
    return issues;
  }

}
